import React, { ComponentType, Fragment, ReactNode } from "react";
import { connect } from "react-redux";

import { getAccessChangeRevision } from "@app/store/accessChange";
import { RootState } from "@app/store/rootReducer";

interface AccessChangeBoundaryProps {
  children: ReactNode;
}

// a new key for each applied access change makes React mount the children afresh
const AccessChangeBoundary = ({
  accessChangeRevision,
  children,
}: AccessChangeBoundaryProps & ReturnType<typeof mapStateToProps>) => (
  <Fragment key={accessChangeRevision}>{children}</Fragment>
);

const mapStateToProps = (state: RootState) => ({
  accessChangeRevision: getAccessChangeRevision(state),
});

const ConnectedAccessChangeBoundary = connect(mapStateToProps)(AccessChangeBoundary);

// For a component that reads lists from the field models once, when it mounts. A refresh after an
// access change updates those models in place, so the component is mounted afresh to read them.
function withRemountOnAccessChange<P extends object>(WrappedComponent: ComponentType<P>) {
  const WithRemountOnAccessChange = (props: P) => (
    <ConnectedAccessChangeBoundary>
      <WrappedComponent {...props} />
    </ConnectedAccessChangeBoundary>
  );

  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
  WithRemountOnAccessChange.displayName = `withRemountOnAccessChange(${displayName})`;

  return WithRemountOnAccessChange;
}

export default withRemountOnAccessChange;
